// Household setup gate — shown in the adult shell when the signed-in
// user belongs to no household yet (no more silent auto-create). It's a
// soft prompt, not a hard block: personal lists, to-dos, and the
// personal calendar all work without a household. Creating one (or being
// added to one by an owner) unlocks the shared shopping/calendar/pantry/
// meal-plan/chores/rewards features.

import { useEffect, useState } from "preact/hooks";
import { api } from "../lib/rpc";
import type { NotifyFn } from "./toast";

export function HouseholdSetupBanner({ notify }: { notify: NotifyFn }) {
  const [needsSetup, setNeedsSetup] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.householdsList()
      .then((r) => { if (!cancelled) setNeedsSetup((r.households || []).length === 0); })
      .catch(() => { /* not fatal — just don't show the banner */ });
    return () => { cancelled = true; };
  }, []);

  if (!needsSetup) return null;

  const create = async () => {
    const name = prompt("Name your household (e.g. The Smiths):");
    if (!name || !name.trim()) return;
    setBusy(true);
    try {
      await api.householdsCreate(name.trim());
      notify("Household created");
      setNeedsSetup(false);
    } catch (e) {
      notify((e as Error)?.message || "couldn't create household");
      setBusy(false);
    }
  };

  return (
    <div className="household-setup-banner">
      <div className="household-setup-banner-text">
        <strong>Set up your household</strong>
        <span>Create one to share shopping, calendar, and chores with your family — or ask an owner to add you. Your personal lists & to-dos work either way.</span>
      </div>
      <button className="household-setup-banner-btn" onClick={create} disabled={busy} type="button">
        {busy ? "Creating…" : "Create household"}
      </button>
    </div>
  );
}
